import {EntityService, EntitySyncRequest} from "./entity.service";
import {Observable, ReplaySubject, Subject, Subscription} from "rxjs";
import {EntityData, EntityDataResolutionRequest} from "./entity.data";
import {Injector, Signal} from "@angular/core";
import {DataResourceModel} from "../data.resource.model";
import {ValidationMessage} from "../../validation/validation.service";
import {DataClientError} from "../../error/data-client-error";
import {toSignal} from "@angular/core/rxjs-interop";

/**
 * Provides an entity model and its dependencies
 * @param modelType
 * @returns {any[]}
 */
export function provideEntityModel(modelType: any): any[] {
    return [
        modelType,
        {provide: EntityModel, useExisting: modelType},
        {provide: DataResourceModel, useExisting: modelType}
    ];
}

export enum EntityModelStatus {
    EDIT, INSERT
}

/**
 * Base class for all entity models. A model represents the resource state, as well as provides operations
 * that can be performed on the resource.
 *
 * A default service implementation is provided for performing server side communication. More specific implementations
 * may be provided via the subclass constructor if required.
 *
 * Generic parameter T defines the type of EntityData to be held by the model.
 *
 * Created by Robert on 13/09/2016.
 */
export abstract class EntityModel<T extends EntityData<T>> extends DataResourceModel<T> {

    /**
     * Define the status of the model.
     */
    public status: EntityModelStatus;
    protected service: EntityService;
    /**
     * Can be subscribed to get the entity data when it is resolved
     * @type {ReplaySubject<any>}
     * @private
     */
    private _$data: Subject<T> = new ReplaySubject(1);
    public $data: Observable<T> = this._$data.asObservable();
    public dataSignal: Signal<T> = toSignal<T>(this._$data, {initialValue: undefined});
    private _$componentLoaded: { [path: string]: Subject<EntityData<any>> } = {};
    private _$collectionLoaded: { [path: string]: Subject<EntityData<any>[]> } = {};
    private _$collectionChanged: { [path: string]: Subject<EntityData<any>[]> } = {};
    /**
     *  Define if the instance is pristine. It is used to avoid creating another instance.
     */
    private pristineInstance: boolean;

    /**
     * Holds the subscription for the collectionResolutions.
     */
    private collectionResolutionsSubscription: Subscription;


    /**
     * Holds the subscription for the componentResolutions.
     */
    private componentResolutionsSubscription: Subscription;

    /**
     * Holds the subscription for componentLoaded.
     */
    private componentLoadedSubscription: Subscription;

    /**
     * Holds the subscription for collectionLoaded.
     */
    private collectionLoadedSubscription: Subscription;

    /**
     * Holds the subscription for collectionChanged.
     */
    private collectionChangedSubscription: Subscription;

    /**
     * Constructs with the service which is used for all server side comms.
     *
     * This constructor must be replicated in subclasses to allow Angular dependency injection to provide the service.
     * Subclasses may also specify a more specialised service that extends EntityService. If so, then the subclass
     * must also override #getEntityService
     *
     * @param injector Injector
     * @param dataType EntityData subclass
     * @param resourceVersion Resource version
     */
    constructor(protected injector: Injector, dataType: typeof EntityData) {
        super(dataType);
        if (injector == null) {
            console.error("!!!! Injector not provided. This is most likely due to a missing constructor in " + this.constructor.toString());
        }
        this.createService();
        this.createEntity(false);
        this.pristineInstance = true;
    }

    public getNameSpace(): string {
        return this.service.getNameSpace();
    }

    public getResource(): string {
        return this.service.getResource();
    }

    /**
     * Initialises this EntityModel with the state from the server
     * @returns {Promise<any>} JSON object representation of the entity
     * @param identifier
     * @param params
     */
    loadEntity(identifier: any, params?: { [key: string]: any }): Promise<any> {
        let p: Promise<any> = this.service.getEntity(identifier, params);
        p.then(json => this.loadData(json)).catch((error) => {
        });
        return p;
    }

    cloneEntity(identifier: any): Promise<any> {
        let p: Promise<any> = this.service.cloneEntity(identifier);
        p.then(json => {
            this.loadData(json, true);
            this.onCloneEntity(this.data);
        }).catch((error) => {
        });
        return p;
    }

    loadCollection(collectionPath: string, actualCollectionPath?: string): Promise<any> {
        let p: Promise<any> = this.service.getCollection(actualCollectionPath || collectionPath);
        p.then(json => this.data.loadCollection(collectionPath, json));
        return p;
    }

    loadComponent(componentPath: string, actualComponentPath?: string): Promise<any> {
        let p: Promise<any> = this.service.getComponent(actualComponentPath || componentPath);
        p.then(json => this.data.loadComponent(componentPath, json));
        return p;
    }

    newEntity() {
        this.status = EntityModelStatus.INSERT;
        if (this.pristineInstance) {
            this.onNewEntity(this.data);
            this._$data.next(this.data);
        } else {
            this.createEntity();
        }
        this.pristineInstance = false;
    }

    /**
     * Persist the state of this model to the server
     * @return (Promise<any>) JSON object containing validation errors
     */
    async saveEntity(): Promise<any> {
        try {
            this.onBeforeSaveEntity(this.data);
            const json: any = await (this.status == EntityModelStatus.INSERT ? this.service.createEntity(this.data) : this.service.updateEntity(this.data));
            this.loadData(json);
            return undefined;
        } catch (error) {
            const validationMessages: ValidationMessage[] = await (<DataClientError> error).validationMessages;
            if (validationMessages) {
                return validationMessages;
            }
            throw error;
        }
    }

    deleteEntity(identifier: any): Promise<string> {
        return this.service.deleteEntity(identifier);
    }

    discontinueEntity(identifier: any): Promise<string> {
        return this.service.discontinueEntity(identifier);
    }

    recontinueEntity(identifier: any): Promise<string> {
        return this.service.recontinueEntity(identifier);
    }

    syncEntity(entitySyncRequest: EntitySyncRequest): Promise<any> {
        return this.service.syncEntity(entitySyncRequest);
    }

    clearEntity(): void {
        this.data = undefined;
        this._$data.next(undefined);
    }

    /**
     * Helper method to allow framework components to check for discontinued entities.
     * The default behaviour is to return the value of a "discontinued" field on the EntityData object held by this model.
     * This method may be overridden by search model implementations, however the preferred approach is to provide an appropriate getter on the EntityData class itself if required.
     * @return true if the entity is discontinued, false if not, or undefined if the entity does not support discontinuation.
     */
    isEntityDiscontinued(): boolean {
        if (!this.data) {
            return undefined;
        }
        return this.data["discontinued"];
    }

    /**
     * Helper method to allow framework components to mark entities as discontinued.
     * The default behaviour is to set the value of a "discontinued" field on the EntityData object held by this model.
     * This method may be overridden by search model implementations, however the preferred approach is to provide an appropriate setter on the EntityData class itself if required.
     * @param discontinued
     */
    setEntityDiscontinued(discontinued: boolean): void {
        if (this.data) {
            this.data["discontinued"] = discontinued;
        }
    }

    /**
     * Indicates that the authenticated user has permission to create the entity
     * @returns {Promise<boolean>}
     */
    public canCreateEntity(): Promise<boolean> {
        return this.service.canCreateEntity();
    }

    /**
     * Indicates that the authenticated user has permission to retrieve the entity
     * @returns {Promise<boolean>}
     */
    public canGetEntity(): Promise<boolean> {
        return this.service.canGetEntity();
    }

    /**
     * Indicates that the authenticated user has permission to update the entity
     * @returns {Promise<boolean>}
     */
    public canUpdateEntity(): Promise<boolean> {
        return this.service.canUpdateEntity();
    }

    /**
     * Indicates that the authenticated user has permission to delete the entity
     * @returns {Promise<boolean>}
     */
    public canDeleteEntity(): Promise<boolean> {
        return this.service.canDeleteEntity();
    }

    /**
     * Indicates that the authenticated user has permission to discontinue/recontinue the entity
     * @returns {Promise<boolean>}
     */
    public canDiscontinueEntity(): Promise<boolean> {
        return this.service.canDiscontinueEntity();
    }

    /**
     * Indicates that the authenticated user has permission to sync the entity to another instance in the group
     * @returns {Promise<boolean>}
     */
    public canSyncEntity(): Promise<boolean> {
        return this.service.canSyncEntity();
    }

    /**
     * Returns an observable that resolves when the specified component is loaded. It will always return the last value for the component.
     * @returns {Observable<EntityData<any>>}
     */
    public $componentLoaded(componentPath: string): Observable<EntityData<any>> {
        if (!this._$componentLoaded[componentPath]) {
            this._$componentLoaded[componentPath] = new ReplaySubject(1);
        }
        return this._$componentLoaded[componentPath].asObservable();
    }

    /**
     * Returns an observable that resolves when the specified collection is assigned or loaded. It will always return the last value for the array.
     * @returns {Observable<EntityData<any>[]>}
     */
    public $collectionLoaded(collectionPath: string): Observable<EntityData<any>[]> {
        if (!this._$collectionLoaded[collectionPath]) {
            this._$collectionLoaded[collectionPath] = new ReplaySubject(1);
        }
        return this._$collectionLoaded[collectionPath].asObservable();
    }

    /**
     * Returns an observable that emits when the specified collection changes including assigning a new value or adding and removing items from the collection.
     * @returns {Observable<EntityData<any>[]>}
     */
    public $collectionChanged(collectionPath: string): Observable<EntityData<any>[]> {
        if (!this._$collectionChanged[collectionPath]) {
            this._$collectionChanged[collectionPath] = new Subject();
        }
        return this._$collectionChanged[collectionPath].asObservable();
    }

    /**
     * Can be overridden to set default values when adding a new entity
     * @param {EntityData<T extends EntityData<T>>} entityData
     */
    protected onNewEntity(entityData: T): void {
    }

    /**
     * Can be overridden to set default values when cloning an entity
     * @param {EntityData<T extends EntityData<T>>} entityData
     */
    protected onCloneEntity(entityData: T): void {
    }

    /**
     * Returns the service type expected by this model (defaults to a generic EntityService).
     * Extending models should override this if a specialised service is required.
     * @returns {EntityService}
     */
    protected getEntityService(): typeof EntityService {
        return EntityService;
    }

    /**
     * Can be overridden to change the entity before saving
     * @param {T} entityData
     */
    protected onBeforeSaveEntity(entityData: T): void {
    }

    /**
     * Can be overridden to change the entity after loading
     * @param {T} entityData
     */
    protected onAfterLoadEntity(entityData: T): void {
    }

    private createService(): void {
        const injector: Injector = Injector.create({
            providers: [
                {
                    provide: EntityService.ENTITY_RESOURCE,
                    useFactory: () => this.getResourceName()
                },
                {
                    provide: EntityService.RESOURCE_VERSION,
                    useFactory: () => this.getResourceVersion()
                },
                this.getEntityService()
            ],
            parent: this.injector
        });
        this.service = injector.get(this.getEntityService());
    }

    /**
     * Create a new instance of the entity, and subscribe to the resolutions events.
     */
    private createEntity(triggerDataChange: boolean = true): void {
        if (this.collectionResolutionsSubscription) {
            this.collectionResolutionsSubscription.unsubscribe();
        }
        if (this.componentResolutionsSubscription) {
            this.componentResolutionsSubscription.unsubscribe();
        }
        if (this.componentLoadedSubscription) {
            this.componentLoadedSubscription.unsubscribe();
        }
        if (this.collectionLoadedSubscription) {
            this.collectionLoadedSubscription.unsubscribe();
        }
        if (this.collectionChangedSubscription) {
            this.collectionChangedSubscription.unsubscribe();
        }

        let entityData: EntityData<T> = EntityData.create(this.dataType);
        this.collectionResolutionsSubscription = entityData.collectionResolutions.subscribe((request: EntityDataResolutionRequest) => {
            this.loadCollection(request.path, request.actualPath)
        });
        this.componentResolutionsSubscription = entityData.componentResolutions.subscribe((request: EntityDataResolutionRequest) => {
            this.loadComponent(request.path, request.actualPath)
        });

        this.componentLoadedSubscription = entityData.componentLoaded.subscribe((
            loaded: {
                path: string,
                component: EntityData<any>
            }
        ) => {
            if (!this._$componentLoaded[loaded.path]) {
                this._$componentLoaded[loaded.path] = new ReplaySubject(1);
            }
            this._$componentLoaded[loaded.path].next(loaded.component);
        });

        this.collectionLoadedSubscription = entityData.collectionLoaded.subscribe((
            loaded: {
                path: string,
                collection: EntityData<any>[]
            }
        ) => {
            if (!this._$collectionLoaded[loaded.path]) {
                this._$collectionLoaded[loaded.path] = new ReplaySubject(1);
            }
            this._$collectionLoaded[loaded.path].next(loaded.collection);
        });

        this.collectionChangedSubscription = entityData.collectionChanged.subscribe((
            changed: {
                path: string,
                collection: EntityData<any>[]
            }
        ) => {
            let subject: Subject<EntityData<any>[]> = this._$collectionChanged[changed.path];
            if (subject) {
                subject.next(changed.collection);
            }
        });

        this.data = <T>entityData;

        if (triggerDataChange) {
            this.onNewEntity(this.data);
            this._$data.next(this.data);
        }
    }

    /**
     * Load the json to the data and set the status to EDIT
     * @param json
     */
    private loadData(json: any, cloning: boolean = false) {
        if (!this.pristineInstance || cloning) {
            this.createEntity(false);
        }
        this.pristineInstance = false;
        if (this.getSerializeToEntityData()) {
            this.data.load(json);
        } else {
            this.data = json;
        }
        this.status = cloning ? EntityModelStatus.INSERT : EntityModelStatus.EDIT;
        this.onAfterLoadEntity(this.data);

        if (cloning) {
            this.canCreateEntity().then((value) => this.readonly = !value);
        } else {
            this.canUpdateEntity().then((value) => this.readonly = !value);
        }

        this._$data.next(this.data);
    }

    isEntityCloneable(): boolean {
        return true;
    }

    isEntityAuditable(): boolean {
        return true;
    }

    notifyDataChange(): void {
        this._$data.next(this.data);
    }

    protected getSerializeToEntityData(): boolean {
        return true;
    }
}
